import { Component, type ErrorInfo, type ReactNode } from "react";
import { detectInitialLang } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/Button";

/**
 * Last-resort catch for render throws and failed lazy-chunk loads (a deploy
 * mid-session invalidates hashed chunk URLs). Sits OUTSIDE I18nProvider so a
 * provider crash is caught too — copy is resolved via detectInitialLang()
 * instead of context, and styles are plain Tailwind classes (global CSS).
 */

const COPY = {
  en: {
    title: "Something went wrong",
    body: "An unexpected error interrupted the app. Reloading usually fixes it.",
    cta: "Reload",
  },
  ro: {
    title: "Ceva n-a mers bine",
    body: "O eroare neașteptată a întrerupt aplicația. De obicei o reîncărcare rezolvă problema.",
    cta: "Reîncarcă",
  },
} as const;

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error("Uncaught render error:", error, info.componentStack);
  }

  override render() {
    if (!this.state.hasError) return this.props.children;
    const copy = COPY[detectInitialLang()];
    return (
      <main className="flex min-h-[100dvh] flex-col items-center justify-center bg-zinc-950 light:bg-white px-6 text-center">
        <h1 className="font-display text-3xl tracking-tight text-zinc-50 light:text-zinc-900">{copy.title}</h1>
        <p className="mt-3 max-w-sm text-pretty text-sm leading-relaxed text-zinc-400 light:text-zinc-600">{copy.body}</p>
        <Button onClick={() => window.location.reload()} className="mt-6 px-5 py-3">
          {copy.cta}
        </Button>
      </main>
    );
  }
}
